import { browser } from "$app/environment";
import type { AisleLayout, AisleLayoutBase, AisleLayoutModel, ProductData } from "$lib/def/landing";
import { createUpgrader, open, transaction } from "$lib/util/db";
import { Subject, distinctUntilChanged, filter, from, map, merge, pairwise, switchMap, tap } from "rxjs";
import { get$, post$, profile$ } from "./core/auth";

/**
 * Fill a layout payload with product details.
 * @param layout The original (unfilled) layout payload.
 * @param store_code If a store ID/code is available.
 * @param baseUrl Allows the caller to use an endpoint for loading static test data (styleguide)
 * @returns `layout`, but with product details now populated.
 */
export function fillLayout(
	layout: (Omit<AisleLayoutModel, "title" | "type"> & { title?: string, type?: string })[],
	store_code?: string,
	baseUrl?: string,
) {
	if (store_code) layout = layout.map(item => ({ ...item, store_code }));
	return post$<AisleLayoutBase>(baseUrl ?? 'ca://grocery/products/fill_layout', layout, true)
	.pipe(
		map(res => mergeFill(layout as AisleLayoutModel[], res)),
	);
}

/**
 * Fill a layout payload with product details concidering cache.
 * @param layout The original (unfilled) layout payload.
 * @param store_code If a store ID/code is available.
 * @param baseUrl Allows the caller to use an endpoint for loading static test data (styleguide)
 * @returns `layout`, but with product details now populated.
 */
export function fillLayoutC(
	// In some cases the layout items don't have details like aisle names (e.g. banners),
	// so `title` and `type` can be omitted so that platform knows to send us those details
	layout: (Omit<AisleLayoutModel, "title" | "type"> & { title?: string, type?: string })[],
	store_code?: string,
	baseUrl?: string,
) {
	if (store_code) layout = layout.map(item => ({ ...item, store_code }));
	const keys = layout.map(item => item.product_selection.catalogue_keys ?? []).reduce((acc, keys) => acc.concat(keys), [] as string[])
	return merge(
		from(getCacheProducts([...new Set(keys)])).pipe(
			map(products => ({
				layout_items: layout.map(item => ({
					...item,
					products: item.product_selection.catalogue_keys?.map(key => products.find(p => p?.catalogue_key === key) ?? null) ?? []
				}))
			} as AisleLayoutBase)),
		),
		post$<AisleLayoutBase>(baseUrl ?? 'ca://grocery/products/fill_layout', layout, true).pipe(
			tap(layout => saveProducts(layout.layout_items.flatMap(item => item.products))),
			map(res => mergeFill(layout as AisleLayoutModel[], res)),
		),
	)
}

function mergeFill(items:AisleLayoutModel[], filled:AisleLayoutBase): AisleLayoutBase {
	return {
		layout_items: items.map(item => ({
			...item,
			...filled.layout_items.find(i => item.sort === i.sort) as AisleLayout,
		}))
	}
}

async function saveProducts(products: ProductData[]) {
	for (const p of products) {
		if (p.url) {
			// The following line will not work as we are loading a URL and not a trusted file (file input/blob)
			// A way this may work is to make use of the image tag used in the browser to load the image (this will also prevent double loading of the image)
			// p.thumb = await getThumbDataURL(`${PUBLIC_PNP_IMG_HOST}/${p.url}`);
			// p.url = p.thumb.dataURL;
			// console.log('product thumb', p.thumb)
		}
		await setCacheProduct(p)
	}
}

/**
  * Fetches a product.
  * @param catalogue_key - The catalogue_key of the product.
  * @returns the product as an observable.
  */
export function fetchProduct(catalogue_key: string) {
	return get$<ProductData>(`ca://grocery/products/catalogue/${catalogue_key}`);
}
/**
 * Fetches a product concidering cache.
  * @param catalogue_key - The catalogue_key of the product.
  * @returns the product as an observable.
 */
export function fetchProductC(catalogue_key: string) {
	return merge(
		from(getCacheProduct(catalogue_key)),
		get$<ProductData>(`ca://grocery/products/catalogue/${catalogue_key}`).pipe(
			tap(product => setCacheProduct(product)),
		)
	);
}

export interface SiloData {
  catalogue_key: string;
  info: Record<string, any>[];
  ingredients: Record<string, any>[];
  nutrition: Record<string, any>[];
}
/**
  * Fetches product silo data. (info, ingredients, nutrition)
  * @param catalogue_key - The catalogue_key of the product.
  * @returns the product silo data as an observable.
  */
export function fetchProductInfo(catalogueKey: string) {
	return get$<SiloData>(
    `ca://grocery/products/catalogue/${catalogueKey}/product_info`
  );
}

/**
  * Fetches product alternatives.
  * @param args - Object containing the product catalogue_key, subaisle_id and store_code.
  * @returns the product alternatives as an observable.
  */
export function fetchAlternatives(args: { catalogue_key: string, subaisle_id: number, store_code: string }) {
	return get$<ProductData[]>(
    `ca://grocery/alternatives/${args.catalogue_key}?subaisle_id=${args.subaisle_id}&store_code=${args.store_code}`
  );
}

/** A way for the service to indicate that a product's saved-ness has been updated */
const savedItemsUpdated = new Subject<ProductData>();
/**
 * Creates a stream of updates about a product's saved-ness (or all products)
 * @param catalogueKey - The specific product that we want to observe; otherwise, we want
 * updates about any and every product whose saved-ness changes
 * @returns A stream with the latest data about the product with `catalogueKey` (or any
 * product if `catalogueKey` is not given).  Emissions happen each time a product's
 * saved status changes.
*/
export const getSavedStatusUpdates = (catalogueKey?: string) => {
	return savedItemsUpdated
		.pipe(
			filter((product) => catalogueKey ? product.catalogue_key === catalogueKey : true),
		);
};

/**
 * Add/Remove a product to/from the logged-in user's saved items list
 * @param item - The product that the user wants to save/remove from saved items list
 * @param action - Whether the user wants to add the product to the saved items list or
 * to remove it
 * @returns An Observable that emits the "latest" (we hope) version of the product after
 * the saved-ness update
 */
export const updateSavedStatus = (item: ProductData, action: "save" | "unsave") => {
	let requestBody: { "products_to_add": string[] }
		| { "products_to_remove": string[] }
		| null = null;

	if (action === "save")
		requestBody = { "products_to_add": [item.catalogue_key] };
	else if (action === "unsave")
		requestBody = { "products_to_remove": [item.catalogue_key] };

	return post$<null>("ca://grocery/products/saved-items", requestBody, true)
		.pipe(
			// switchMap(() => get$<ProductData>(`ca://grocery/products/catalogue/${item.catalogue_key}`, true)),
			// HACK to avoid having to re-fetch the [possibly-not-updated-yet] product
			map(() => ({
				...item,
				user_specific: {
					last_bought: item.user_specific?.last_bought ?? "", // This is unfortunate; last_bought is actually an optional field in the proto
					saved_list: action === "save" ? true : false,
				},
			})),
			tap((updatedProduct) => { savedItemsUpdated.next(updatedProduct); }),
		);
};

// ############################################################
// DB FUNCTIONS
// ############################################################

const DBNAME = 'groc-products';

const upgrader = createUpgrader({
	1: (db) => {
		const os = db.createObjectStore('products', { keyPath: ['catalogue_key'] });
		os.createIndex('store_code', 'store_code', { unique: false });
		os.createIndex('product_code', 'product_code', { unique: false });
		return [os];
	},
});
const pdb = browser ? open(DBNAME, 1, upgrader) : Promise.resolve(null);

/**
 * Clear all object stores from the database.
 * @returns Promise that resolves when all object stores have been cleared.
 */
export async function clearDB() {
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for clearing.');
	}
	if (!db) return;
	const oss = ['products'];
	const t = transaction(db, oss, 'readwrite');
	return Promise.all(oss.map(os => t.os(os).clear())).then(() => console.log(`Cleared [${oss.join(', ')}] of IDB ${DBNAME}.`));
}

/**
 * Clear DB on logout and login.
 */
profile$.pipe(
	map(user => !!user),
	distinctUntilChanged(),
	pairwise(), // effectivley skipping the first emit
).subscribe(() => clearDB());

/**
 * Get a product form indexedDB.
 * @param catalogueKey Store product's catalogue_key
 * @returns The product data or null if not found.
 */
async function getCacheProduct(catalogueKey:string, ): Promise<ProductData|null> {
	if (!catalogueKey) return null;
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for caching products.');
	}
	if (!db) return null;
	return transaction(db, 'products', 'readonly').os('products').get<ProductData>(catalogueKey);
}

/**
 * Get products from indexedDB.
 * @param catalogueKeys	Array of catalogue_keys of products to get.
 * @returns	An array with each element being either product data or null if not found.
 */
async function getCacheProducts(catalogueKeys:string[]): Promise<(ProductData|null)[]> {
	if (!catalogueKeys?.length) return [];
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for reading products cache.');
	}
	if (!db) return [];
	const os = transaction(db, 'products', 'readonly').os('products');
	const promises = catalogueKeys.map(catalogueKey => os.get<ProductData>(catalogueKey));
	const settlement = await Promise.allSettled(promises);
	return settlement.map(p => p.status === 'fulfilled' ? p.value : null);
}

/**
 * Set a product in the cache.
 * @param product The product to set in the cache.
 */
async function setCacheProduct(product?:ProductData) {
	if (!product) return null;
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for updating product cache.');
	}
	if (!db) return;
	const os = transaction(db, 'products', 'readwrite').os('products');
	return os.put(product);
}

/**
 * Set products in the cache.
 * @param products	Array of products to set in the cache.
 */
async function setCacheProducts(products?:ProductData[]) {
	if (!products?.length) return [];
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for updating products cache.');
	}
	if (!db) return;
	const os = transaction(db, 'products', 'readwrite').os('products');
	const promises = products.map(product => os.put(product));
	const settlement = await Promise.allSettled(promises);
	return settlement.map(p => p.status === 'fulfilled' ? p.value : null);
}
